import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { gql } from '@apollo/client';
import type { AsStoreObject, DeepPartial } from '@apollo/client/utilities';
import type { ConnectDragPreview } from 'react-dnd';
import { useDrag, useDrop } from 'react-dnd';
import { Apollo } from 'src/gql/generated';
import { DateKey, documentTypename, getDateComparator } from 'src/helpers';
import {
  useAreWorkflowStepsEditable,
  useCheckWorkflowAuthorization,
  useGetWorkflowSimple,
  useGetWorkflowWithSteps,
  useMoveWorkflowStepActions,
} from 'src/hooks';
import type { ActionWithStep, WorkflowRecipientAction, WorkflowStep } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

interface UseWorkflow {
  loading: boolean;
  steps: WorkflowStep[];
  workflow: NonNullable<Apollo.GetWorkflowQueryHookResult['data']>['workflow'];
}

type WorkflowWithSigningPhase = {
  phases: Array<{ __typename: string } | { __typename: 'SigningPhase'; steps: WorkflowStep[] }>;
};

/**
 * Extracts ordered signing steps from a workflow's phases.
 */
export function getSigningStepsFromWorkflow(
  workflow: WorkflowWithSigningPhase | null | undefined
): WorkflowStep[] {
  if (!workflow) return [];
  const signingPhase = workflow.phases.find(
    (p): p is { __typename: 'SigningPhase'; steps: WorkflowStep[] } =>
      p.__typename === 'SigningPhase'
  );
  return signingPhase?.steps ?? [];
}

/**
 * Query for and provide workflow steps to the workflow component.
 */
export function useWorkflow(
  workflowId: UUID | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetWorkflowQuery>[0]>, 'variables'>
): UseWorkflow {
  const { loading, workflow } = useGetWorkflowWithSteps(workflowId, options);
  return {
    loading,
    steps: getSigningStepsFromWorkflow(workflow),
    workflow,
  };
}

/**
 * Create a workflow using `initializeWorkflow`.
 */
export function useCreateWorkflow() {
  const [createWorkflow, { loading }] = Apollo.useCreateWorkflowForDocumentsMutation();

  return {
    createWorkflow: (
      options: Parameters<typeof Apollo.useCreateWorkflowForDocumentsMutation>[0] & {
        variant: Apollo.WorkflowVariant;
      }
    ) => {
      // Options should include variables from caller
      return createWorkflow({
        ...(options as Parameters<typeof createWorkflow>[0]),
        update: (cache, { data }) => {
          const newWorkflowRef = cache.writeFragment({
            data: data?.initializeWorkflow.workflow,
            fragment: gql`
              fragment NewWorkflow on Workflow {
                __typename
                id
              }
            `,
          });
          cache.modify({
            fields: {
              workflows: (existingWorkflowsInfo, { readField }) => {
                if (!existingWorkflowsInfo || !newWorkflowRef) return existingWorkflowsInfo;

                const existingWorkflows =
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  readField<any[]>(
                    'rows' satisfies keyof Apollo.WorkflowRowsInfo,
                    existingWorkflowsInfo
                  ) ?? [];
                const existingMeta = readField<Apollo.WorkflowsMeta>(
                  'meta' satisfies keyof Apollo.WorkflowRowsInfo,
                  existingWorkflowsInfo
                );
                const safeExistingCount = existingMeta?.totalCount ?? 0;

                return {
                  ...existingWorkflowsInfo,
                  __typename: 'WorkflowRowsInfo',
                  meta: {
                    ...existingMeta,
                    __typename: 'WorkflowsMeta',
                    totalCount: safeExistingCount + 1,
                  },
                  rows: [...existingWorkflows, newWorkflowRef],
                } satisfies DeepPartial<AsStoreObject<Apollo.WorkflowRowsInfo>>;
              },
            },
          });

          const workflowKey: keyof Apollo.Document = 'workflow';
          options?.variables?.input?.documentIds?.forEach(documentId => {
            cache.modify({
              fields: {
                [workflowKey]: () => newWorkflowRef ?? null,
              },
              id: cache.identify({ __typename: documentTypename, id: documentId }),
            });
          });
        },
      });
    },
    loading,
  };
}

export function getSortedStepActions(step: WorkflowStep): WorkflowRecipientAction[] {
  return [...step.actions].sort(getDateComparator({ dateKey: DateKey.createdAt }));
}

interface StyleProps {
  isDraggable: boolean;
  isDragging: boolean;
}
const useDraggingStyles = makeStyles<StyleProps>()((_, { isDraggable, isDragging }) => ({
  dragStyles: {
    cursor: isDragging ? 'grabbing' : isDraggable ? 'grab' : undefined,
    // Style needed to prevent drag preview for expandable elements from rendering incorrectly.  See https://github.com/react-dnd/react-dnd/issues/832#issuecomment-442071628.
    transform: 'translate3d(0, 0, 0)',
  },
}));

interface UseDragActionCollectedProps {
  isDraggable: boolean;
  isDragging: boolean;
}

/**
 * Handles dragging of actions between/within other steps.
 */
export function useDragAction(
  workflowId: UUID,
  action: ActionWithStep
): {
  drag: [UseDragActionCollectedProps, React.RefCallback<HTMLElement>, ConnectDragPreview];
  dragClasses: ReturnType<typeof useDraggingStyles>['classes'];
} {
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const [collectedProps, connectDragSource, connectDragPreview] = useDrag<
    ActionWithStep,
    unknown,
    UseDragActionCollectedProps
  >({
    canDrag: areStepsEditable && canEditWorkflow,
    collect: monitor => ({
      isDraggable: monitor.canDrag(),
      isDragging: monitor.isDragging(),
    }),
    item: action, // The thing(s) being moved.
    type: action.__typename, // This is matched against the accept of useDrop.
  });

  // Convert ConnectDragSource to a proper React ref callback for React 19 compatibility
  const dragRef = useCallback<React.RefCallback<HTMLElement>>(
    node => {
      if (node) connectDragSource(node);
    },
    [connectDragSource]
  );

  const { isDraggable, isDragging } = collectedProps;
  const { classes: dragClasses } = useDraggingStyles({ isDraggable, isDragging });

  return { drag: [collectedProps, dragRef, connectDragPreview], dragClasses };
}

interface UseDropActionCollectedProps {
  isOver: boolean;
}

/**
 * Handles dropping of Actions between/within other steps in a Workflow.
 *
 * @param workflowId The workflow in which the dropping happens.
 * @param targetStepId The step in/around which the drop would occur. If step is empty, always accept a drop.
 * @param actionPlacement The placement type of the moved action group wrt the target step.
 * @param accept The acceptable type of the thing being dropped.
 */
export function useDropAction({
  actionPlacement,
  targetStepId,
  workflowId,
}: {
  actionPlacement: Apollo.ActionPlacement;
  targetStepId: UUID | null;
  workflowId: UUID;
}): [UseDropActionCollectedProps, React.RefCallback<HTMLElement>] {
  const { moveWorkflowStepActions } = useMoveWorkflowStepActions();
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);

  const [dropActionCollectedProps, connectDropTarget] = useDrop<
    ActionWithStep,
    unknown,
    UseDropActionCollectedProps
  >({
    // String or array of strings of the acceptable types.
    accept: ['WorkflowRecipient'] satisfies [WorkflowRecipientAction['__typename']],
    // Feeds the monitor.canDrop() hook.
    canDrop: action => {
      const moveGroupBeforeOrAfterTarget =
        actionPlacement === Apollo.ActionPlacement.BeforeStep ||
        actionPlacement === Apollo.ActionPlacement.AfterStep;
      const actionAlreadyInStep = action.step.id === targetStepId;
      const isValidMove = moveGroupBeforeOrAfterTarget || !actionAlreadyInStep;

      return areStepsEditable && canEditWorkflow && isValidMove;
    },
    // Collected props of the useDrop hook.
    collect: monitor => ({
      isOver: monitor.canDrop() && monitor.isOver(),
    }),
    // What happens when a drop occurs. Callback gets the items being dragged and the monitor singleton
    drop: async (action, monitor) => {
      if (!monitor.canDrop()) return;

      await moveWorkflowStepActions({
        variables: {
          input: {
            actionPlacement,
            targetStepId,
            workflowId,
            workflowStepActionIds: [action.id],
          },
        },
      });
    },
  });

  // Convert ConnectDropTarget to a proper React ref callback for React 19 compatibility
  const dropRef = useCallback<React.RefCallback<HTMLElement>>(
    node => {
      if (node) connectDropTarget(node);
    },
    [connectDropTarget]
  );

  return [dropActionCollectedProps, dropRef];
}

/**
 * Determine whether a given Workflow action has been completed.
 */
function getIsWorkflowActionComplete(
  action: Pick<Apollo.WorkflowAction, 'completedAt' | '__typename'>
): boolean {
  return Boolean(action.completedAt);
}

/**
 * Determine whether a WorkflowStep is complete.
 */
export function getIsWorkflowStepComplete(step: WorkflowStep): boolean {
  return step.actions.every(getIsWorkflowActionComplete);
}

/**
 * Determines whether workflow steps are active or complete based on active step and workflow status.
 */
export function useAreWorkflowStepsActiveOrComplete(workflowId: UUID): boolean {
  const { workflow } = useGetWorkflowSimple(workflowId);
  const wasSentForSig = getWorkflowWasSentForSig(workflow);
  const isWorkflowComplete = workflow?.status === Apollo.WorkflowStatus.Completed;

  return wasSentForSig || isWorkflowComplete;
}

/** Spots a signing phase from its GraphQL typename, for getIsWorkflowPhased. */
export function getIsSigningPhaseType(phase: { __typename?: string }): boolean {
  return phase.__typename === 'SigningPhase';
}

/**
 * Determines whether a workflow was sent for signature.
 */
export function getWorkflowWasSentForSig(
  workflow: Pick<Apollo.Workflow, 'state' | '__typename'> | null
): boolean {
  if (!workflow) return false;

  switch (workflow.state) {
    case Apollo.WorkflowState.Signing:
    case Apollo.WorkflowState.Signed:
    case Apollo.WorkflowState.Complete:
      return true;
    case Apollo.WorkflowState.Negotiating:
    case Apollo.WorkflowState.ReadyForSignature:
    case Apollo.WorkflowState.Template:
      return false;
  }
}

/**
 * Determines whether a workflow is signed.
 */
export function getIsWorkflowSigned(workflow: Pick<Apollo.Workflow, 'state'> | null): boolean {
  if (!workflow) return false;

  switch (workflow.state) {
    case Apollo.WorkflowState.Signed:
    case Apollo.WorkflowState.Complete:
      return true;
    case Apollo.WorkflowState.Negotiating:
    case Apollo.WorkflowState.ReadyForSignature:
    case Apollo.WorkflowState.Signing:
    case Apollo.WorkflowState.Template:
      return false;
  }
}

// Colors
export enum WorkflowStateColors {
  CompletedGreen = '#00870E',
  DraftingYellow = '#FCDB62',
  ReadyBlue = '#146EE8',
  SentOrange = '#FEB06D',
  SignedGreen = '#00866A',
  TemplateGrey = '#324A52',
}

export function getWorkflowStateColor(
  workflowState?: Apollo.WorkflowState | null
): WorkflowStateColors {
  if (!workflowState) return WorkflowStateColors.DraftingYellow;

  switch (workflowState) {
    case Apollo.WorkflowState.Template:
      return WorkflowStateColors.TemplateGrey;
    case Apollo.WorkflowState.Complete:
      return WorkflowStateColors.CompletedGreen;
    case Apollo.WorkflowState.Signing:
      return WorkflowStateColors.SentOrange;
    case Apollo.WorkflowState.Signed:
      return WorkflowStateColors.SignedGreen;
    case Apollo.WorkflowState.Negotiating:
      return WorkflowStateColors.DraftingYellow;
    case Apollo.WorkflowState.ReadyForSignature:
      return WorkflowStateColors.ReadyBlue;
  }
}

export function getCanDocsBeAddedToWorkflow(
  workflow: Pick<Apollo.Workflow, 'state'> | null
): boolean {
  if (!workflow) return false;

  switch (workflow.state) {
    case Apollo.WorkflowState.Negotiating:
    case Apollo.WorkflowState.ReadyForSignature:
    case Apollo.WorkflowState.Template:
      return true;
    case Apollo.WorkflowState.Signing:
    case Apollo.WorkflowState.Signed:
    case Apollo.WorkflowState.Complete:
      return false;
  }
}

export function getWorkflowIsSigned(workflow: Pick<Apollo.Workflow, 'state'> | null) {
  if (!workflow) return false;

  switch (workflow.state) {
    case Apollo.WorkflowState.Complete:
    case Apollo.WorkflowState.Signed:
      return true;
    default:
      return false;
  }
}

export function getWorkflowStateDisplayString(state: Apollo.WorkflowState): string {
  switch (state) {
    case Apollo.WorkflowState.Negotiating:
      return 'Draft';
    case Apollo.WorkflowState.ReadyForSignature:
      return 'Ready for Signature';
    case Apollo.WorkflowState.Signing:
      return 'Signing';
    case Apollo.WorkflowState.Signed:
      return 'Signed';
    case Apollo.WorkflowState.Complete:
      return 'Complete';
    case Apollo.WorkflowState.Template:
      return 'Template';
  }
}
